import type { FunctionComponent } from 'react';
import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Combobox,
  Group,
  ScrollAreaAutosize,
  Text,
  useCombobox,
} from '@mantine/core';

import { useTranslation } from 'i18n';
import { ChevronDownIconXs } from '@chownow/pantry/icons';

const NS = 'menuPhotos';

export const ITEM_STATUS = {
  ACTIVE: 'active',
  HIDDEN: 'hidden',
} as const;

export type StatusValue = (typeof ITEM_STATUS)[keyof typeof ITEM_STATUS];

export const SORT_ORDER = {
  MENU: 'menu',
  ITEM_NAME: 'item_name',
} as const;

export type SortValue = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export type PhotoFiltersProps = {
  onStatusChange: (statuses: StatusValue[]) => void;
  onSortChange: (sort: SortValue) => void;
};

export const STATUS_VALUES = [ITEM_STATUS.ACTIVE, ITEM_STATUS.HIDDEN] as const;
const ALL_STATUSES: StatusValue[] = [...STATUS_VALUES];
const SORT_VALUES = [SORT_ORDER.MENU, SORT_ORDER.ITEM_NAME] as const;

export const PhotoFilters: FunctionComponent<PhotoFiltersProps> = ({
  onStatusChange,
  onSortChange,
}) => {
  const { t } = useTranslation(NS);
  const [pending, setPending] = useState<StatusValue[]>(ALL_STATUSES);
  const [applied, setApplied] = useState<StatusValue[]>(ALL_STATUSES);
  const [activeSort, setActiveSort] = useState<SortValue>(SORT_ORDER.MENU);
  const combobox = useCombobox({
    onDropdownClose: () => setPending([...applied]),
  });
  const sortCombobox = useCombobox();

  const statusLabels: Record<StatusValue, string> = {
    [ITEM_STATUS.ACTIVE]: t('PhotoFilters.activeItems'),
    [ITEM_STATUS.HIDDEN]: t('PhotoFilters.hiddenItems'),
  };

  const sortLabels: Record<SortValue, string> = {
    [SORT_ORDER.MENU]: t('PhotoFilters.byMenuOrder'),
    [SORT_ORDER.ITEM_NAME]: t('PhotoFilters.byItemName'),
  };

  const allChecked = pending.length === ALL_STATUSES.length;

  const getButtonLabel = () => {
    if (!applied.length || applied.length === ALL_STATUSES.length) {
      return t('PhotoFilters.allItems');
    }
    if (applied.length === 1) {
      return statusLabels[applied[0]];
    }
    return t('PhotoFilters.itemTypes', { count: applied.length });
  };
  const buttonLabel = getButtonLabel();

  const toggleAll = () => {
    setPending(allChecked ? [] : ALL_STATUSES);
  };

  const toggleOption = (val: StatusValue) => {
    setPending(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]));
  };

  const onDone = () => {
    setApplied(pending);
    onStatusChange(pending);
    combobox.closeDropdown();
  };

  const handleSortSelect = (sort: SortValue) => {
    sortCombobox.closeDropdown();
    if (sort === activeSort) return;
    setActiveSort(sort);
    onSortChange(sort);
  };

  return (
    <ButtonGroup w="fit-content">
      <Combobox store={combobox} position="bottom-start">
        <Combobox.Target>
          <Button variant="ghost" size="sm" onClick={() => combobox.openDropdown()}>
            <Group gap="xs2">
              <Text variant="caption-xs">{buttonLabel}</Text>
              <ChevronDownIconXs />
            </Group>
          </Button>
        </Combobox.Target>
        <Combobox.Dropdown miw={280}>
          <ScrollAreaAutosize type="scroll" mah={300}>
            <Combobox.Options py="xs3">
              <Combobox.Option
                value="all"
                px="sm"
                py="xs"
                bg="transparent"
                style={{ cursor: 'default' }}
              >
                <Group gap="xs2">
                  <Checkbox
                    checked={allChecked}
                    onChange={toggleAll}
                    tabIndex={-1}
                    aria-label={t('PhotoFilters.allItems')}
                  />
                  <Text style={{ cursor: 'text' }}>{t('PhotoFilters.allItems')}</Text>
                </Group>
              </Combobox.Option>
              {STATUS_VALUES.map(value => (
                <Combobox.Option
                  key={value}
                  value={value}
                  px="sm"
                  py="xs"
                  bg="transparent"
                  style={{ cursor: 'default' }}
                >
                  <Group gap="xs2">
                    <Checkbox
                      checked={pending.includes(value)}
                      onChange={() => toggleOption(value)}
                      tabIndex={-1}
                      aria-label={statusLabels[value]}
                    />
                    <Text style={{ cursor: 'text' }}>{statusLabels[value]}</Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </ScrollAreaAutosize>
          <Button size="sm" fullWidth mt="sm" onClick={onDone}>
            {t('PhotoFilters.done')}
          </Button>
        </Combobox.Dropdown>
      </Combobox>
      <Combobox
        store={sortCombobox}
        position="bottom-start"
        onOptionSubmit={val => handleSortSelect(val as SortValue)}
      >
        <Combobox.Target>
          <Button
            variant="ghost"
            size="sm"
            data-active={activeSort !== SORT_ORDER.MENU || undefined}
            onClick={() => sortCombobox.toggleDropdown()}
          >
            <Group gap="xs2">
              <Text variant="caption-xs">{sortLabels[activeSort]}</Text>
              <ChevronDownIconXs />
            </Group>
          </Button>
        </Combobox.Target>
        <Combobox.Dropdown miw={280} p="xs2">
          <Combobox.Options>
            {SORT_VALUES.map(sort => (
              <Combobox.Option
                key={sort}
                value={sort}
                px="sm"
                py="xs"
                bg={sort === activeSort ? 'var(--surface-paper-3)' : undefined}
              >
                <Text c={sort === activeSort ? 'var(--content-accent-primary)' : undefined}>
                  {sortLabels[sort]}
                </Text>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </ButtonGroup>
  );
};
